import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Card, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import {
  DEFAULT_THRESHOLDS,
  WeatherThresholds,
  TemperatureCategory,
  WindCategory,
  PrecipitationCategory,
  categorizeTemperature,
  categorizeWind,
  categorizePrecipitation,
} from '../../services/weatherCategorization';
import { getCachedWeather } from '../../services/weatherCacheService';
import { readDraft, writeDraft } from '../../services/sprayCompletionDraft';
import { conditionToIcon } from '../WeatherDisplay';

/**
 * OpenSignOffWeatherPanel — weather panel mounted on the Spray Module's
 * Open Sign-off page (FieldModePreFlightForm). Mirrors the Close Sign-off
 * weather panel inside FieldModeCompletionForm but is sourced from the
 * spray-side capture endpoint so it can be used by Operators (who do not
 * carry Settings module access).
 *
 * Responsibilities:
 *   1. POST /api/v1/tenants/me/weather/capture-for-spray on mount to write
 *      a fresh WeatherHistory row for the configured Farm Location and
 *      pull back the raw values.
 *   2. If that call returns 400 farm_location_not_set, surface a Settings-
 *      directed alert and switch to manual-only mode (radios still active
 *      so the operator can sign off, per the Reliability NFR).
 *   3. If it fails for any other reason, fall back to the cached-weather
 *      entry the PWA's hourly cron already mirrored into IndexedDB.
 *   4. Auto-categorise the raw values via weatherCategorization and pre-
 *      select the matching radio in each of the three groups (Temperature,
 *      Wind, Precipitation), but never clobber a draft selection the
 *      operator already made on a previous visit.
 *   5. Persist the operator's category selections to the per-session
 *      draft (sprayCompletionDraft) under start*Category keys, so leaving
 *      the page and coming back restores the choices. Distinct from the
 *      close-sign-off's *Category keys so the two forms don't clobber
 *      each other.
 *   6. Render a Verified (green) or Manual override (yellow) badge next
 *      to each category legend, comparing the operator's selection to
 *      the system-categorised value. Mirrors the Close Sign-off panel
 *      so the audit cue is consistent across the two screens.
 *   7. Emit `onChange({ systemReported?, finalSelected })` whenever the
 *      raw values or any radio selection changes. The shape mirrors the
 *      input contract of sprayWeatherService.captureSnapshot so the parent
 *      can submit it as the start-of-session snapshot without further
 *      transformation.
 *
 * Track: spray_weather_enhancements_20260429 / Phase 2.
 */

export interface SprayWeatherSnapshotInput {
  systemReported?: {
    temp: number;
    windSpeed: number;
    precipitation: number;
  };
  finalSelected: {
    temperature: TemperatureCategory | '';
    wind: WindCategory | '';
    precipitation: PrecipitationCategory | '';
  };
}

interface RawWeather {
  timestamp?: string;
  temp?: number;
  windSpeed?: number;
  precipitation?: number;
  condition?: string;
  humidity?: number;
  pressure?: number;
}

interface Props {
  onChange: (snapshot: SprayWeatherSnapshotInput) => void;
  /**
   * Per-session id. When provided, the panel restores the operator's
   * previous category selections from sprayCompletionDraft on mount and
   * writes them back as the operator picks. Without it, persistence is
   * a no-op (useful for unit tests of the panel in isolation).
   */
  sessionId?: string;
  /**
   * Datetime-local string for the operator's intended start time.
   * Drives the source-of-truth choice:
   *   - empty / within 30 min of "now" → POST capture-for-spray (live)
   *   - 30 min – 48 h ago → GET /weather/recent and apply the closest
   *     historical row, so the snapshot reflects the conditions at the
   *     backdated start time, not at form mount.
   *   - > 48 h ago → POST capture-for-spray (no historical data in
   *     window; cache fallback / manual-only paths apply).
   * Track: spray_weather_enhancements_20260429 / Phase 5 wiring fix
   * (round 3).
   */
  startTime?: string;
  /**
   * Tenant thresholds for auto-categorisation. Defaults to DEFAULT_THRESHOLDS
   * if the parent has not loaded the tenant doc — matches the backend's
   * captureSnapshot fallback.
   */
  thresholds?: WeatherThresholds;
  /**
   * Compact Mode opt-in (F5). Adds `.ntr-fol-weather` to the panel root and
   * nothing else — every visual change lives in nutrition.css under that class.
   *
   * Deliberately className-only: a structural branch under `compact` is the
   * thing that would break Protection, and neither this panel's 21 tests nor
   * FieldModePreFlightForm's / OperatorComboWorkFlow's would see it. With one
   * expression and one branch, "Protection renders byte-identically" is
   * provable rather than asserted.
   *
   * The .ntr-fol-* class appears inside components/spray/ on purpose: it is
   * applied ONLY when the foliar caller opts in, and that caller imports
   * nutrition.css.
   */
  compact?: boolean;
}

// Track: spray_weather_enhancements_20260429 / Phase 5 wiring fix (round 3).
// Hysteresis: a startTime within 30 min of "now" still uses the live
// capture path. Without this band the panel would flip to the historical
// branch on every keystroke as the operator nudges Start Time around.
const HISTORICAL_THRESHOLD_MS = 30 * 60 * 1000;
// 48 h matches the WeatherAPI history endpoint's effective window and the
// backend's /me/weather/recent cap.
const HISTORY_WINDOW_MS = 48 * 60 * 60 * 1000;

const formatNumber = (n?: number): string =>
  typeof n === 'number' ? `${n}` : '—';

const TEMP_OPTIONS: { value: TemperatureCategory; key: string }[] = [
  { value: 'Cold', key: 'spray.openSignOff.weather.tempCold' },
  { value: 'Optimal', key: 'spray.openSignOff.weather.tempOptimal' },
  { value: 'Hot', key: 'spray.openSignOff.weather.tempHot' },
];
const WIND_OPTIONS: { value: WindCategory; key: string }[] = [
  { value: 'Calm', key: 'spray.openSignOff.weather.windCalm' },
  { value: 'Breeze', key: 'spray.openSignOff.weather.windBreeze' },
  { value: 'Strong', key: 'spray.openSignOff.weather.windStrong' },
];
const PRECIP_OPTIONS: { value: PrecipitationCategory; key: string }[] = [
  { value: 'None', key: 'spray.openSignOff.weather.precipNone' },
  { value: 'Light', key: 'spray.openSignOff.weather.precipLight' },
  { value: 'Heavy', key: 'spray.openSignOff.weather.precipHeavy' },
];

interface RadioGroupProps<V extends string> {
  legend: string;
  ariaLabel: string;
  name: string;
  testIdSuffix: string;
  options: { value: V; key: string }[];
  value: V | '';
  systemValue: V | null;
  onChange: (next: V) => void;
  t: (key: string) => string;
}

function CategoryRadioGroup<V extends string>({
  legend,
  ariaLabel,
  name,
  testIdSuffix,
  options,
  value,
  systemValue,
  onChange,
  t,
}: RadioGroupProps<V>) {
  const verified = Boolean(systemValue && value && systemValue === value);
  const overridden = Boolean(systemValue && value && systemValue !== value);

  return (
    <fieldset className="mt-3" aria-label={ariaLabel}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <legend className="form-label mb-0 fw-semibold">{legend}</legend>
        {verified && (
          <Badge
            bg="success"
            data-testid={`os-weather-status-${testIdSuffix}`}
          >
            <i className="bi bi-check-circle-fill me-1" aria-hidden="true" />
            {t('spray.openSignOff.weather.verified')}
          </Badge>
        )}
        {overridden && (
          <Badge
            bg="warning"
            text="dark"
            data-testid={`os-weather-status-${testIdSuffix}`}
          >
            <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true" />
            {t('spray.openSignOff.weather.override')}
          </Badge>
        )}
      </div>
      <div className="d-flex flex-wrap gap-3 mt-2">
        {options.map(opt => (
          <Form.Check
            key={opt.value}
            type="radio"
            id={`${name}-${opt.value}`}
            name={name}
            label={t(opt.key)}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
        ))}
      </div>
    </fieldset>
  );
}

const OpenSignOffWeatherPanel: React.FC<Props> = ({
  onChange,
  sessionId,
  startTime,
  thresholds = DEFAULT_THRESHOLDS,
  compact = false,
}) => {
  const { t } = useTranslation();

  // Pull any previously-saved selections out of the per-session draft
  // synchronously on first render, so the radios reflect the operator's
  // earlier choices without flickering through the auto-pre-selected
  // values when the live capture lands a moment later.
  const initialDraft = sessionId ? readDraft(sessionId) : null;

  const [loading, setLoading] = useState(true);
  const [raw, setRaw] = useState<RawWeather | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [farmLocationMissing, setFarmLocationMissing] = useState(false);
  const [manualOnly, setManualOnly] = useState(false);

  const [temperature, setTemperature] = useState<TemperatureCategory | ''>(
    (initialDraft?.startTemperatureCategory as TemperatureCategory | undefined) ?? ''
  );
  const [wind, setWind] = useState<WindCategory | ''>(
    (initialDraft?.startWindCategory as WindCategory | undefined) ?? ''
  );
  const [precipitation, setPrecipitation] = useState<PrecipitationCategory | ''>(
    (initialDraft?.startPrecipitationCategory as PrecipitationCategory | undefined) ?? ''
  );

  // Tracks which categories were restored from the draft on mount; the
  // raw-value auto-categorisation must NOT clobber those, otherwise an
  // operator's previous override would be silently discarded the moment
  // the live capture returned.
  const restoredFromDraft = useRef<Set<'temperature' | 'wind' | 'precipitation'>>(
    new Set([
      ...(initialDraft?.startTemperatureCategory ? ['temperature' as const] : []),
      ...(initialDraft?.startWindCategory ? ['wind' as const] : []),
      ...(initialDraft?.startPrecipitationCategory ? ['precipitation' as const] : []),
    ])
  );

  // Capture flow: pick the source-of-truth endpoint based on how stale
  // the parent's startTime prop is, then apply the closest reading to
  // the radio pre-selection + raw-value display. The effect re-runs on
  // every startTime change — typing into the parent's datetime-local
  // input rolls out a fresh fetch (historical or live) so the panel's
  // categorisation tracks the operator's intent, not just form mount.
  //
  // Source matrix:
  //   - empty / within 30 min of "now" → POST capture-for-spray (live)
  //   - 30 min – 48 h ago               → GET /weather/recent, closest
  //                                       row to startTime
  //   - > 48 h ago                      → POST capture-for-spray
  //                                       (no historical data in window)
  //
  // Failure paths are unchanged from round-1: 400 farm_location_not_set
  // → settings-pointed alert; any other error → PWA cache fallback,
  // then manual-only mode. Both fallbacks still render the radios so
  // the operator can sign off (Reliability NFR).
  useEffect(() => {
    let cancelled = false;

    const applyRaw = (entry: RawWeather, opts: { fromCache?: boolean } = {}) => {
      if (cancelled) return;
      setRaw(entry);
      setFromCache(Boolean(opts.fromCache));
      // Auto-pre-select each category that the operator hasn't already
      // chosen. The draft (read synchronously on mount) wins over the
      // raw-value auto-categorisation.
      if (typeof entry.temp === 'number' && !restoredFromDraft.current.has('temperature')) {
        setTemperature(categorizeTemperature(entry.temp, thresholds.temperature));
      }
      if (typeof entry.windSpeed === 'number' && !restoredFromDraft.current.has('wind')) {
        setWind(categorizeWind(entry.windSpeed, thresholds.wind));
      }
      if (typeof entry.precipitation === 'number' && !restoredFromDraft.current.has('precipitation')) {
        setPrecipitation(categorizePrecipitation(entry.precipitation, thresholds.precipitation));
      }
    };

    const target = startTime ? Date.parse(startTime) : NaN;
    const ageMs = Number.isFinite(target) ? Date.now() - target : 0;
    const wantHistorical =
      Number.isFinite(target) &&
      ageMs > HISTORICAL_THRESHOLD_MS &&
      ageMs <= HISTORY_WINDOW_MS;

    setLoading(true);
    setFarmLocationMissing(false);
    setManualOnly(false);
    setFromCache(false);

    const run = wantHistorical
      ? axios
          .get<{ entries: RawWeather[] }>(
            '/api/v1/tenants/me/weather/recent',
            {
              params: { hours: 48 },
              withCredentials: true,
            }
          )
          .then(res => {
            const entries = res?.data?.entries || [];
            if (entries.length === 0) return;
            const closest = entries.reduce((best, cur) => {
              const bd = Math.abs(Date.parse(best.timestamp ?? '') - target);
              const cd = Math.abs(Date.parse(cur.timestamp ?? '') - target);
              return cd < bd ? cur : best;
            });
            applyRaw(closest);
          })
      : axios
          .post('/api/v1/tenants/me/weather/capture-for-spray', undefined, {
            withCredentials: true,
          })
          .then((res: any) => {
            const entry = res?.data?.entry;
            if (entry) applyRaw(entry);
          });

    run
      .catch((err: any) => {
        if (cancelled) return;
        const status = err?.response?.status;
        const code = err?.response?.data?.error;
        if (status === 400 && code === 'farm_location_not_set') {
          setFarmLocationMissing(true);
          return;
        }
        // Any other failure → cache fallback.
        return getCachedWeather()
          .then(cached => {
            if (cancelled) return;
            if (cached) {
              applyRaw(cached as RawWeather, { fromCache: true });
            } else {
              setManualOnly(true);
            }
          })
          .catch(() => {
            if (cancelled) return;
            setManualOnly(true);
          });
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // thresholds is stable from DEFAULT_THRESHOLDS in the typical mount
    // and is intentionally read on the very first render. Re-running on
    // every thresholds change would mean firing capture-for-spray on
    // every parent re-render — not what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime]);

  // System-categorised values, derived from the raw reading. Used both
  // by the badge logic (Verified vs Manual override) and as the source
  // of truth for the auto-pre-selection above.
  const systemTemperature: TemperatureCategory | null =
    raw && typeof raw.temp === 'number'
      ? categorizeTemperature(raw.temp, thresholds.temperature)
      : null;
  const systemWind: WindCategory | null =
    raw && typeof raw.windSpeed === 'number'
      ? categorizeWind(raw.windSpeed, thresholds.wind)
      : null;
  const systemPrecipitation: PrecipitationCategory | null =
    raw && typeof raw.precipitation === 'number'
      ? categorizePrecipitation(raw.precipitation, thresholds.precipitation)
      : null;

  // Persist the current category selections to the per-session draft
  // whenever they change, merging on top of whatever the close sign-off
  // (or pre-flight PPE write) may have already saved. The raw weather
  // reading is also persisted so the close sign-off form can build a
  // complete weatherSnapshots.start.systemReported payload at /complete
  // time without re-fetching. No-op when the panel is mounted without a
  // sessionId (e.g. unit tests in isolation).
  useEffect(() => {
    if (!sessionId) return;
    const existing = readDraft(sessionId);
    writeDraft(sessionId, {
      ...(existing ?? {}),
      startTemperatureCategory: temperature || undefined,
      startWindCategory: wind || undefined,
      startPrecipitationCategory: precipitation || undefined,
      startWeatherReading: raw
        ? {
            timestamp: raw.timestamp,
            temp: raw.temp,
            windSpeed: raw.windSpeed,
            precipitation: raw.precipitation,
            condition: raw.condition,
            humidity: raw.humidity,
            pressure: raw.pressure,
          }
        : existing?.startWeatherReading,
    });
  }, [sessionId, temperature, wind, precipitation, raw]);

  // Emit the snapshot whenever raw values or any selection changes.
  useEffect(() => {
    const snapshot: SprayWeatherSnapshotInput = {
      finalSelected: { temperature, wind, precipitation },
    };
    if (raw && typeof raw.temp === 'number' && typeof raw.windSpeed === 'number' && typeof raw.precipitation === 'number') {
      snapshot.systemReported = {
        temp: raw.temp,
        windSpeed: raw.windSpeed,
        precipitation: raw.precipitation,
      };
    }
    onChange(snapshot);
    // onChange is intentionally omitted — parents typically pass an inline
    // arrow which would re-fire this effect on every render. The snapshot
    // value is the source of truth; identity-only changes to onChange
    // should not re-emit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, temperature, wind, precipitation]);

  if (loading) {
    return (
      <Card className={compact ? 'mb-3 ntr-fol-weather' : 'mb-3'} data-testid="os-weather-loading">
        <Card.Body className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" role="status" />
          <span>{t('spray.openSignOff.weather.loading')}</span>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className={compact ? 'mb-3 ntr-fol-weather' : 'mb-3'} data-testid="os-weather-panel">
      <Card.Body>
        <h2 className="h5">{t('spray.openSignOff.weather.title')}</h2>

        {farmLocationMissing && (
          <Alert variant="warning" data-testid="os-weather-no-location" className="mb-3">
            {t('spray.openSignOff.weather.noFarmLocation')}
          </Alert>
        )}
        {manualOnly && !farmLocationMissing && (
          <Alert variant="info" data-testid="os-weather-manual-only" className="mb-3">
            {t('spray.openSignOff.weather.manualOnly')}
          </Alert>
        )}
        {fromCache && (
          <Alert variant="info" data-testid="os-weather-from-cache" className="mb-3">
            {t('spray.openSignOff.weather.fromCache')}
          </Alert>
        )}

        {raw && (
          <div className="d-flex align-items-start gap-3 mb-2">
            <i
              className={`bi ${conditionToIcon(raw.condition)}`}
              data-testid="os-weather-icon"
              style={{ fontSize: '2.25rem' }}
              aria-hidden="true"
            />
            <div className="d-flex flex-wrap gap-3 small">
              <span data-testid="os-weather-temp">
                <strong>{t('spray.openSignOff.weather.tempLabel')}:</strong>{' '}
                {formatNumber(raw.temp)} °C
              </span>
              <span data-testid="os-weather-wind">
                <strong>{t('spray.openSignOff.weather.windLabel')}:</strong>{' '}
                {formatNumber(raw.windSpeed)} kph
              </span>
              <span data-testid="os-weather-precip">
                <strong>{t('spray.openSignOff.weather.precipLabel')}:</strong>{' '}
                {formatNumber(raw.precipitation)} mm
              </span>
              <span data-testid="os-weather-humidity">
                <strong>{t('spray.openSignOff.weather.humidityLabel')}:</strong>{' '}
                {formatNumber(raw.humidity)} %
              </span>
              <span data-testid="os-weather-pressure">
                <strong>{t('spray.openSignOff.weather.pressureLabel')}:</strong>{' '}
                {formatNumber(raw.pressure)} mb
              </span>
              {raw.condition && (
                <span data-testid="os-weather-condition" className="text-muted">
                  {raw.condition}
                </span>
              )}
            </div>
          </div>
        )}

        <CategoryRadioGroup<TemperatureCategory>
          legend={t('spray.openSignOff.weather.tempLegend')}
          ariaLabel={t('spray.openSignOff.weather.tempLegend')}
          name="os-temperature"
          testIdSuffix="temperature"
          options={TEMP_OPTIONS}
          value={temperature}
          systemValue={systemTemperature}
          onChange={setTemperature}
          t={t}
        />
        <CategoryRadioGroup<WindCategory>
          legend={t('spray.openSignOff.weather.windLegend')}
          ariaLabel={t('spray.openSignOff.weather.windLegend')}
          name="os-wind"
          testIdSuffix="wind"
          options={WIND_OPTIONS}
          value={wind}
          systemValue={systemWind}
          onChange={setWind}
          t={t}
        />
        <CategoryRadioGroup<PrecipitationCategory>
          legend={t('spray.openSignOff.weather.precipLegend')}
          ariaLabel={t('spray.openSignOff.weather.precipLegend')}
          name="os-precipitation"
          testIdSuffix="precipitation"
          options={PRECIP_OPTIONS}
          value={precipitation}
          systemValue={systemPrecipitation}
          onChange={setPrecipitation}
          t={t}
        />
      </Card.Body>
    </Card>
  );
};

export default OpenSignOffWeatherPanel;
